import { useState } from 'react';
import Button from '@/components/Button';
import ToggleSwitch from '@/components/ToggleSwitch';
import { NullIsland } from '@/models/Geolocation';
import {
  findSupplierAddress,
  Supplier,
  SupplierAddressType,
  SupplierCreate,
  SupplierUpdate,
} from '@/models/Supplier';
import OrganizationSelectBox from '@/modules/admin/components/OrganizationSelectBox';
import SupplierAddressForm from '@/modules/supplier/components/SupplierAddressForm';
import { CheckIcon } from '@heroicons/react/24/solid';
import { FormProvider, useForm } from 'react-hook-form';

interface SupplierFormProps {
  supplier?: Supplier;
  activeAddressType?: SupplierAddressType.BUSINESS | SupplierAddressType.BILLING;
  mode?: 'details' | 'address';
  onSubmit: (supplier: SupplierCreate | SupplierUpdate) => Promise<void>;
}

const getAddressLabel = (type: SupplierAddressType.BUSINESS | SupplierAddressType.BILLING) =>
  type === SupplierAddressType.BILLING ? 'Billing Address' : 'Business Address';

const sectionClassName = 'rounded-2xl border border-slate-200 bg-white p-6 lg:p-8';

const SupplierForm = ({
  supplier,
  activeAddressType = SupplierAddressType.BUSINESS,
  mode = 'details',
  onSubmit,
}: SupplierFormProps) => {
  const [loading, setLoading] = useState(false);

  const defaultBusinessAddress = {
    supplierId: supplier?.id || '',
    type: SupplierAddressType.BUSINESS,
    location: { ...NullIsland },
    building: '',
    street1: '',
    street2: '',
    postalCode: '',
    region: '',
    city: '',
    countryCode: '',
    additionalDescription: '',
  };

  const defaultBillingAddress = {
    supplierId: supplier?.id || '',
    type: SupplierAddressType.BILLING,
    location: { ...NullIsland },
    building: '',
    street1: '',
    street2: '',
    postalCode: '',
    region: '',
    city: '',
    countryCode: '',
    additionalDescription: '',
  };

  const activeAddress =
    findSupplierAddress(supplier?.addresses, [activeAddressType]) ||
    (activeAddressType === SupplierAddressType.BILLING
      ? defaultBillingAddress
      : defaultBusinessAddress);

  const methods = useForm<SupplierCreate | SupplierUpdate>({
    defaultValues: supplier
      ? { ...supplier }
      : {
          id: '',
          code: '',
          organizationId: '',
          name: '',
          email: '',
          externalCode: '',
          creditTerm: '',
          status: 'ACTIVE',
          addresses: [],
          userId: '',
        },
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = methods;

  const status = watch('status');

  const handleStatusChange = (checked: boolean) => {
    setValue('status', checked ? 'ACTIVE' : 'INACTIVE');
  };

  const onSubmitForm = async (data: SupplierCreate | SupplierUpdate) => {
    setLoading(true);
    try {
      await onSubmit(data);
    } finally {
      setLoading(false);
    }
  };

  const handleOrganizationChange = (org?: { id: string } | null) => {
    setValue('organizationId', org?.id || '');
  };

  const organizationId = watch('organizationId');

  if (mode === 'address') {
    return (
      <div className="grid grid-cols-2 gap-6 lg:gap-8">
        <div className={`col-span-2 ${sectionClassName}`}>
          <div className="mb-6 border-b border-slate-200 pb-4">
            <h2 className="text-lg font-semibold text-slate-900">
              {getAddressLabel(activeAddressType)}
            </h2>
          </div>

          <SupplierAddressForm
            address={activeAddress}
            supplierId={supplier?.id}
            supplierAddressType={activeAddressType}
            onSubmit={async (address) => {
              if (!supplier || !('id' in supplier)) {
                return;
              }

              const updatedSupplier = {
                ...supplier,
                addresses: [
                  ...(supplier.addresses || []).filter((item) => item.type !== address.type),
                  address,
                ],
              };

              await onSubmit(updatedSupplier);
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmitForm)}>
        <div className="grid grid-cols-2 gap-6 lg:gap-8">
          <div className={`col-span-2 ${sectionClassName}`}>
            <div className="mb-6 border-b border-slate-200 pb-4">
              <h2 className="text-lg font-semibold text-slate-900">Supplier details</h2>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="lg:col-span-2">
                <label htmlFor="name" className="block font-medium text-gray-700">
                  Name:
                </label>
                <input
                  id="name"
                  {...register('name', { required: 'Name is required' })}
                  className="mt-1 p-2 border border-gray-300 rounded-md w-full"
                />
                {errors.name && <span className="text-red-500">{errors.name.message}</span>}
              </div>

              <div className="lg:col-span-2">
                <OrganizationSelectBox value={organizationId} onSelect={handleOrganizationChange} />
              </div>

              <div className="lg:col-span-2">
                <label htmlFor="email" className="block font-medium text-gray-700">
                  Email:
                </label>
                <input
                  id="email"
                  {...register('email', {
                    required: false,
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                  className="mt-1 p-2 border border-gray-300 rounded-md w-full"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Email address where this supplier receives order notifications.
                </p>
                {errors.email && <span className="text-red-500">{errors.email.message}</span>}
              </div>

              <div className="lg:col-span-2">
                <ToggleSwitch
                  checked={status === 'ACTIVE'}
                  onChange={handleStatusChange}
                  labelText="Status:"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <Button
            type="submit"
            icon={<CheckIcon className="h-6 w-6" aria-hidden="true" />}
            loadingIconClassName="h-5 w-5 mr-1"
            variant="primary"
            loading={loading}
          >
            Save
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default SupplierForm;
